import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useCallback } from "react";
import { UseFormReturn } from "react-hook-form";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { SubcontaDropdown } from "../../../../domain/models/subconta_dropdown";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { ModalContainer } from "../../../../../../common/components/modal_container";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { ItemFolhaPagamentoForm } from "../../../schemas/FolhaPagamento_cadastro/item_folha_pagamento";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FolhaPagamento } from "../../../../domain/models/folha_pagamento";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * CadastroItemFolhaPagamentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  idFuncionarioUsuario: number | null,
  selectedFolhaPagamento: FolhaPagamento | null,
  centroCustos: CentroCustoDropDown[],
  funcionarios: FuncionarioDropDown[],
  funcionariosCadastro: FuncionarioDropDown[],
  subcontas: SubcontaDropdown[],
  reactHookForm: UseFormReturn<ItemFolhaPagamentoForm>,
  onSubmit: (form: ItemFolhaPagamentoForm) => void,
  onClose: () => void,
};

export const CadastroItemFolhaPagamentoModal = ({
  open,
  saving,
  idFuncionarioUsuario,
  selectedFolhaPagamento,
  centroCustos,
  funcionarios,
  funcionariosCadastro,
  subcontas,
  reactHookForm,
  onSubmit,
  onClose,
}: Props) => {

  useEffect(() => {
    if (open) {
      reactHookForm.reset({
        dataPagamento: selectedFolhaPagamento && selectedFolhaPagamento.dataReferencia
          ? selectedFolhaPagamento.dataReferencia
          : undefined,
      });

      if (idFuncionarioUsuario) {
        reactHookForm.setValue('idResponsavel', idFuncionarioUsuario);
      }
    }
  }, [
    idFuncionarioUsuario,
    reactHookForm,
    open,
    selectedFolhaPagamento,
  ]);

  const handleSubmit = useCallback((form: ItemFolhaPagamentoForm) => {
    if (!saving) {
      if (form.idFuncionarios.length === 0) {
        reactHookForm.setError('idFuncionarios', { message: 'É necessário preencher este campo!' });
        return;
      }
      onSubmit(form);
    }
  }, [
    reactHookForm,
    saving,
    onSubmit,
  ]);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
    }
  }, [
    saving,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: reactHookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>

      <DialogTitle>
        Cadastrar Pagamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <ModalContainer>
            <FormContainer title={"Informações"} arrangement="fullRow" border={false}>
              <>
                <DefaultFormAutocomplete
                  name='idCentroCusto'
                  label='Centro de Custo'
                  options={centroCustos}
                  getOptionKey={x => x.id}
                  getOptionLabel={x => x.title}
                  reactHookForm={reactHookForm}
                />

                <DefaultFormMultiAutocomplete
                  name='idFuncionarios'
                  label='Funcionários'
                  options={funcionariosCadastro}
                  getOptionKey={x => x.idFuncionario}
                  getOptionLabel={x => x.nome}
                  reactHookForm={reactHookForm}
                />

                <DefaultFormAutocomplete
                  name='idResponsavel'
                  label='Responsável'
                  options={funcionarios}
                  getOptionDisabled={x => !x.canBeSelected}
                  getOptionKey={x => x.idFuncionario}
                  getOptionLabel={x => x.nome}
                  reactHookForm={reactHookForm}
                />

                <FormDatePickerInput
                  name='dataPagamento'
                  label='Data de Pagamento'
                  reactHookForm={reactHookForm}
                />

                <DefaultFormAutocomplete
                  name='idSubconta'
                  label='Subconta'
                  options={subcontas}
                  getOptionKey={x => x.id}
                  getOptionLabel={x => x.title}
                  reactHookForm={reactHookForm}
                />
              </>
            </FormContainer>
          </ModalContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div></div>

          <div className="flex flex-row">
            <div className="flex flex-row gap-2">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={handleClose}>
                  Cancelar
                </h1>
              </div>

              <div className="w-[120px]">
                {saving ? (
                  <PrimaryButton
                    buttonColor="primary-green">
                    Salvando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    buttonColor="primary-green">
                    Salvar
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}